import React, { useState, useRef, useId, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

export interface ImprovedTooltipProps {
  label?: React.ReactNode;
  content?: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  variant?: "light" | "dark";
  className?: string;
  tooltipClassName?: string;
  delay?: number;
  closeOnScroll?: boolean; // New flag
}

const VARIANT_CLASSES: Record<"light" | "dark", string> = {
  light: "bg-white text-slate-900 border border-slate-200 shadow-xl",
  dark: "bg-slate-900 text-white border border-slate-800 shadow-xl",
};

const calculatePosition = (
  rect: DOMRect,
  side: "top" | "bottom" | "left" | "right"
) => {
  const spacing = 8;
  let top = 0;
  let left = 0;

  switch (side) {
    case "top":
      top = rect.top - spacing;
      left = rect.left + rect.width / 2;
      break;
    case "bottom":
      top = rect.bottom + spacing;
      left = rect.left + rect.width / 2;
      break;
    case "left":
      top = rect.top + rect.height / 2;
      left = rect.left - spacing;
      break;
    case "right":
      top = rect.top + rect.height / 2;
      left = rect.right + spacing;
      break;
  }

  return { top, left };
};

const getTransformClass = (side: "top" | "bottom" | "left" | "right") => {
  switch (side) {
    case "top": return "-translate-x-1/2 -translate-y-full";
    case "bottom": return "-translate-x-1/2";
    case "left": return "-translate-x-full -translate-y-1/2";
    case "right": return "-translate-y-1/2";
    default: return "-translate-x-1/2 -translate-y-full";
  }
};

const SimpleTooltip: React.FC<ImprovedTooltipProps> = ({
  label,
  content,
  children,
  side = "bottom",
  variant = "light",
  className,
  tooltipClassName,
  delay = 0,
  closeOnScroll = true, // Default to true for backwards compatibility
}) => {
  const displayLabel = label ?? content;
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    position: { top: number; left: number };
  }>({ isOpen: false, position: { top: 0, left: 0 } });

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const tooltipId = useId();

  // --- Handlers ---
  const handleMouseEnter = () => {
    timeoutRef.current = window.setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const position = calculatePosition(rect, side);
        setTooltipState({ isOpen: true, position });
      }
    }, delay);
  };

  const closeTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };
  const handleMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as Node;

    if (
      triggerRef.current?.contains(relatedTarget) ||
      tooltipRef.current?.contains(relatedTarget)
    ) {
      return;
    }

    closeTooltip();
  };
  const handleBlur = closeTooltip;

  const handleFocus = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const position = calculatePosition(rect, side);
      setTooltipState({ isOpen: true, position });
    }
  };

  useEffect(() => {
    if (!tooltipState.isOpen || !closeOnScroll) return;

    const handleScroll = () => {
      closeTooltip();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("touchmove", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("touchmove", handleScroll);
    };
  }, [tooltipState.isOpen, closeOnScroll]);

  return (
    <div
      ref={triggerRef}
      className={`inline-block relative ${className || ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-describedby={tooltipState.isOpen ? tooltipId : undefined}
    >
      {children}

      {tooltipState.isOpen && displayLabel &&
        createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="tooltip"
            className={cn(
              `fixed transform ${getTransformClass(side)}
              z-[9999999]
              px-3 py-1.5 text-xs font-medium rounded-lg
              max-w-sm text-left break-words whitespace-normal
              animate-tooltip-fade-in pointer-events-none`,
              VARIANT_CLASSES[variant],
              tooltipClassName,
            )}
            style={{
              top: tooltipState.position.top,
              left: tooltipState.position.left,
            }}
          >
            {displayLabel}
          </span>,
          document.body
        )}
    </div>
  );
};

export default SimpleTooltip;
